import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AddressSelectModal } from './AddressSelectModal';
import { LineInfo } from './LineInfo';
import { Table } from './Table';
import { Button } from './buttons/Button';
import { axios } from '../api';
import { createAddress, deleteAddress, updateAddress } from '../api/address';
import { Close } from '../assets/Close';
import { Pencil } from '../assets/Pencil';
import { useEstimatedDeliveryDate } from '../hooks/useShippingRates';
import { Address, Customer, Order } from '../pages/types';
import { ReplacementOrderHeader } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  formatAddress,
  formatDeliveryDateEstimates,
  formatDeliveryDateEstimatesFromShippingOptions,
  generateErrorMsg,
  generateFullName,
  SHIPMENT_DISPLAY_VALUE,
} from '../utils';

interface ModificationOrderProps {
  customer: Customer;
  order: Order;
  onConfirm: () => void;
  onCancel: () => void;
}

const formatShippingData = (
  shippingAddress: string | JSX.Element,
  shippingMethod: string,
  estimatedDeliveryDates: string
) => {
  return (
    <div className="flex-col text-start">
      <div className="flex flex-row">
        <strong className="mr-1 min-w-[144px]">Shipping Address:</strong> {shippingAddress}
      </div>
      <div className="flex flex-row">
        <strong className="mr-1">Shipping Method:</strong> {shippingMethod}
      </div>
      <div className="flex flex-row">
        <strong className="mr-1">Estimated Delivery Date:</strong> {estimatedDeliveryDates}
      </div>
    </div>
  );
};

const getOrderProductData = (order: Order) => {
  return order.products.map((product: any, index: number) => {
    return {
      'Pet Name': product.pet_plan?.pet_name,
      Quantity: product.quantity,
      'Product Code': <div className="max-w-[180px]">{product.code}</div>,
      'Product Description': (
        <div className="max-w-[722px]">{product.description_bark ?? '--'}</div>
      ),
      index,
      ...product,
    };
  });
};

export const ModificationOrderModal = ({
  customer,
  order,
  onConfirm,
  onCancel,
}: ModificationOrderProps) => {
  const getOriginalShippingData = (order: Order) => {
    const orderInfo = formatShippingData(
      originalShippingAddress ? formatAddress(originalShippingAddress) : '--',
      SHIPMENT_DISPLAY_VALUE[order?.shipping_method ?? ''] ?? '--',
      formatDeliveryDateEstimates(order)
    );
    return [{ 'Original Order': orderInfo }];
  };

  const getNewShippingData = (order: Order) => {
    const shippingAddress =
      newShippingAddress !== undefined ? (
        formatAddress(newShippingAddress)
      ) : (
        <strong
          className="underline cursor-pointer mr-1"
          onClick={() => setShowAddressSelectModal(true)}
        >
          Select or add new shipping address
        </strong>
      );
    const shippingAddressWithPencilJsx = (
      <div className="flex flex-row items-center gap-1">
        {shippingAddress}
        <Pencil
          className="w-4 h-4 cursor-pointer"
          onClick={() => setShowAddressSelectModal(true)}
        />
      </div>
    );
    const estimatedDeliveryDates =
      newShippingAddress && !isLoadingShippingRates
        ? formatDeliveryDateEstimatesFromShippingOptions(
            shippingOptions,
            order?.shipping_method ?? undefined
          )
        : '--';

    const orderInfo = formatShippingData(
      shippingAddressWithPencilJsx,
      SHIPMENT_DISPLAY_VALUE[order?.shipping_method ?? ''] ?? '--',
      estimatedDeliveryDates
    );
    return [{ 'New Order': orderInfo }];
  };

  const onSubmit = async () => {
    // Snapshot the address state so we can roll back if the order fails to create.
    const previousPrimaryAddress = customer.addresses.find(
      (address) => address.type === 'SHIPPING' && address.is_primary
    );
    let createdAddressId: string | undefined = undefined;

    // 1. If a new address is selected (not existing address for customer),
    // create address object for it
    let newShippingAddressAsPrimary = newShippingAddress as Address;
    if (isSelectedAddressNew) {
      // Cast is safe because the button is disabled until newShippingAddress exists
      try {
        const created = await createAddress(newShippingAddress as Partial<Address>);
        newShippingAddressAsPrimary = created?.data as Address;
        createdAddressId = newShippingAddressAsPrimary.id;
        setNewShippingAddress(newShippingAddressAsPrimary);
      } catch (e) {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
        setIsSubmitting(false);
        return;
      }
    }

    newShippingAddressAsPrimary.is_primary = true;
    let parentOrderUpdated = false;
    try {
      // 2. Set the selected address as primary
      await updateAddress(newShippingAddressAsPrimary.id, newShippingAddressAsPrimary);

      // 3. Update parent order
      await axios.patch(`orders/${order.id}/update_is_modified`, {
        is_modified: true,
      });
      parentOrderUpdated = true;

      // 4. Create Modification order. It will ship to the new address because we wait
      // for it to be set as primary.
      await axios.post('orders', {
        account: customer.id,
        order_products: order.products.map((orderProduct) => {
          return {
            type: 'MODIFICATION',
            parent_order_product: orderProduct.id,
            product_code: orderProduct.code,
            pet_plan: orderProduct.pet_plan?.id,
            quantity: orderProduct.quantity,
          };
        }),
        order_type: 'MODIFICATION',
        parent_order: order.id,
        shipping_method: order?.shipping_method,
      });
      toast.success('New order successfully created.', BOTTOM_TOAST);
      const fullName = generateFullName(customer.first_name, customer.last_name, '_') ?? '';
      setTimeout(() => {
        navigate(`/customer-lookup/${customer.id}__${fullName}`);
      }, 5000);
      onConfirm();
    } catch (e) {
      toast.error(generateErrorMsg(e), BOTTOM_TOAST);

      try {
        if (parentOrderUpdated) {
          await axios.patch(`orders/${order.id}/update_is_modified`, {
            is_modified: false,
          });
        }
      } catch (rollbackError) {
        toast.error(
          `Error while rolling back parent order is_modified: ${generateErrorMsg(rollbackError)}`,
          BOTTOM_TOAST
        );
      }

      // Roll back the address changes made above before the order failed.
      try {
        if (previousPrimaryAddress) {
          previousPrimaryAddress.is_primary = true;
          await updateAddress(previousPrimaryAddress.id, previousPrimaryAddress);
        }
      } catch (rollbackError) {
        toast.error(
          `Error while rolling back primary address: ${generateErrorMsg(rollbackError)}`,
          BOTTOM_TOAST
        );
      }

      try {
        if (createdAddressId) {
          await deleteAddress(createdAddressId);
          setNewShippingAddress(undefined);
        }
      } catch (rollbackError) {
        toast.error(
          `Error while rolling back creating new address: ${generateErrorMsg(rollbackError)}`,
          BOTTOM_TOAST
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddressSelectModal, setShowAddressSelectModal] = useState(false);
  const [originalShippingAddress, setOriginalShippingAddress] = useState<Address | undefined>(
    undefined
  );
  const [newShippingAddress, setNewShippingAddress] = useState<Address | undefined>(undefined);
  const [isSelectedAddressNew, setIsSelectedAddressNew] = useState(false);
  const productsHasFrozen = order.products.some((selectedProduct) => selectedProduct.is_frozen);
  const productsHasAmbient = order.products.some((selectedProduct) => !selectedProduct.is_frozen);

  const navigate = useNavigate();

  useEffect(() => {
    if (order.fulfillments.length > 0) {
      const { address1, address2, city, state, zip, country } = order.fulfillments[0];
      setOriginalShippingAddress(
        customer.addresses.find(
          (address) =>
            address1.toLocaleLowerCase() === address.address1.toLocaleLowerCase() &&
            address2?.toLocaleLowerCase() === address.address2?.toLocaleLowerCase() &&
            city.toLocaleLowerCase() === address.city.toLocaleLowerCase() &&
            state.toLocaleLowerCase() === address.state.toLocaleLowerCase() &&
            zip.toLocaleLowerCase() === address.zip.toLocaleLowerCase() &&
            country.toLocaleLowerCase() === address.country.toLocaleLowerCase()
        )
      );
    }
  }, [order.fulfillments, customer.addresses]);

  const { shippingOptions, isLoadingShippingRates } = useEstimatedDeliveryDate(
    customer.id,
    productsHasFrozen,
    productsHasAmbient,
    /* zipCodeOverride = */ newShippingAddress?.zip
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center m-4 z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-4 min-w-[700px] w-[1100px] relative border overflow-y-auto max-h-[90vh]">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <div className="flex flex-col gap-[24px] w-full">
          <div>
            <h1 className="font-bold text-left">Send Order to a Different Address</h1>
            <LineInfo
              title="Customer"
              value={`${generateFullName(customer.first_name, customer.last_name)} (${
                customer.email
              })`}
            />
          </div>
          <div>
            <h3 className="font-bold text-lg">Shipping</h3>
            <div className="flex flex-row gap-[16px]">
              <div className="flex-1 min-w-0 max-w-[580px]">
                <Table header={['Original Order']} data={getOriginalShippingData(order)} />
              </div>
              <div className="flex-1 min-w-0 max-w-[580px]">
                <Table header={['New Order']} data={getNewShippingData(order)} />
              </div>
            </div>
          </div>
          <div className="w-full">
            <h3 className="font-bold text-lg">Products</h3>
            <div className="w-full">
              <Table header={ReplacementOrderHeader} data={getOrderProductData(order)} />
            </div>
          </div>
          <div className="flex justify-center py-4">
            <Button
              isDisabled={newShippingAddress === undefined || isSubmitting}
              label="Confirm"
              variant={newShippingAddress === undefined ? 'secondary' : 'primary'}
              onClick={() => {
                setIsSubmitting(true);
                onSubmit();
              }}
            />
          </div>
        </div>
      </div>
      {showAddressSelectModal && (
        <AddressSelectModal
          customer={customer}
          originalAddress={originalShippingAddress}
          preselectedAddress={newShippingAddress}
          onConfirm={(address: Address, shouldCreateNewAddress: boolean) => {
            setNewShippingAddress(address);
            setIsSelectedAddressNew(shouldCreateNewAddress);
            setShowAddressSelectModal(false);
          }}
          onCancel={() => setShowAddressSelectModal(false)}
        />
      )}
    </div>
  );
};
